import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { apiClient } from "@/shared/api/client";
import { nikolaRunSessions } from "@/features/chat/messaging/use-nikola-run";
import { PRODUCT_TOUR_VERSION, type AccountProfile, type PlayerProfile } from "@chessla/shared";
import { acceptCurrentTerms } from "@/shared/api/terms.api";
import { TERMS_ACCEPTANCE_STORAGE_KEY, TERMS_VERSION } from "@/shared/constants/legal";

const isE2eMockAuth = import.meta.env.VITE_E2E_MOCK_AUTH === "true";
const e2eMockProfile: PlayerProfile = {
  id: "e2e-user",
  username: "e2e-player",
  platform: "chess.com",
  ratings: {},
  record: {},
  repertoire: { white: [], black: [] },
  weaknesses: [],
  strengths: [],
  goals: [],
  sessionsCount: 0,
  lastSessionAt: null,
  chessCom: {},
  createdAt: "2026-01-01T00:00:00.000Z",
  updatedAt: "2026-01-01T00:00:00.000Z",
};

interface MeResponse {
  userId: string;
  email: string;
  profile: PlayerProfile | null;
  hasAnalyzedGames: boolean;
  onboardingCompleted: boolean;
  account: AccountProfile;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userId: string | null;
  email: string | null;
  account: AccountProfile | null;
  profile: PlayerProfile | null;
  hasAnalyzedGames: boolean;
  onboardingCompleted: boolean;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [hasAnalyzedGames, setHasAnalyzedGames] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const authRevision = useRef(0);

  const checkAuth = useCallback(async () => {
    const revision = ++authRevision.current;
    const current = () => revision === authRevision.current;
    // Visual tests need to exercise the guest-only routes as well as the
    // authenticated product shell. This query is honored only by the explicit
    // Vite E2E build flag and cannot affect a deployed app.
    if (isE2eMockAuth && new URLSearchParams(window.location.search).has("e2eGuest")) {
      nikolaRunSessions.setOwner(null);
      setIsAuthenticated(false);
      setUserId(null);
      setEmail(null);
      setAccount(null);
      setProfile(null);
      setHasAnalyzedGames(false);
      setOnboardingCompleted(false);
      return;
    }
    if (isE2eMockAuth) {
      nikolaRunSessions.setOwner(e2eMockProfile.id);
      setUserId(e2eMockProfile.id);
      setEmail("e2e-player@chessla.test");
      setAccount({
        firstName: "Test",
        lastName: "Player",
        birthdate: "1990-01-01",
        productTourVersion: PRODUCT_TOUR_VERSION,
      });
      setProfile(e2eMockProfile);
      setHasAnalyzedGames(false);
      setOnboardingCompleted(true);
      setIsAuthenticated(true);
      return;
    }

    try {
      // Check for valid Cognito session
      await fetchAuthSession({ forceRefresh: false });
      if (!current()) return;

      // Session exists — call /auth/me to get/create user record
      const me = await apiClient<MeResponse>("/auth/me");
      if (!current()) return;
      if (localStorage.getItem(TERMS_ACCEPTANCE_STORAGE_KEY) === TERMS_VERSION) {
        try {
          await acceptCurrentTerms();
          if (!current()) return;
          localStorage.removeItem(TERMS_ACCEPTANCE_STORAGE_KEY);
        } catch {
          // The user remains authenticated; a later session retry records the agreement.
        }
      }
      if (!current()) return;
      nikolaRunSessions.setOwner(me.userId);
      setUserId(me.userId);
      setEmail(me.email);
      setAccount(me.account);
      setProfile(me.profile ?? null);
      setHasAnalyzedGames(me.hasAnalyzedGames);
      setOnboardingCompleted(me.onboardingCompleted);
      setIsAuthenticated(true);
    } catch (err) {
      if (!current()) return;
      console.error("[AuthProvider] Authentication check failed:", err);
      nikolaRunSessions.setOwner(null);
      setIsAuthenticated(false);
      setUserId(null);
      setEmail(null);
      setAccount(null);
      setProfile(null);
      setHasAnalyzedGames(false);
      setOnboardingCompleted(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    const pending = checkAuth();
    const revision = authRevision.current;
    await pending;
    if (revision === authRevision.current) setIsLoading(false);
  }, [checkAuth]);

  useEffect(() => {
    const revision = authRevision;
    void refetch();
    return () => {
      ++revision.current;
    };
  }, [refetch]);

  const logout = async () => {
    ++authRevision.current;
    nikolaRunSessions.setOwner(null);
    // Clear local state first
    setIsLoading(false);
    setIsAuthenticated(false);
    setUserId(null);
    setEmail(null);
    setAccount(null);
    setProfile(null);
    setHasAnalyzedGames(false);
    setOnboardingCompleted(false);

    // Then sign out from Cognito
    try {
      const { signOut } = await import("aws-amplify/auth");
      await signOut({ global: true });
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        userId,
        email,
        account,
        profile,
        hasAnalyzedGames,
        onboardingCompleted,
        logout,
        refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
